/** Botões do mapa (zoom, voltar à Amazônia) e coordenadas do cursor. */
import clsx from 'clsx';
import { House, Minus, Plus } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import { formatLonLat } from '../lib/format';
import { useMapInstance } from '../map/mapContext';
import { mapToLonLat } from '../map/projection';
import { flyHome, zoomBy } from '../map/viewControl';
import { useHasHover } from './useMediaQuery';

function ControlButton(props: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={props.label}
      title={props.label}
      onClick={props.onClick}
      className="inline-flex size-9 items-center justify-center text-ink-100 transition-colors pointer-coarse:size-11 hover:bg-ink-700/80 hover:text-white focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400"
    >
      {props.children}
    </button>
  );
}

/** Coordenadas do cursor em lat/lon (atualizadas sem React; ocultas fora do disco da Terra). */
function CursorCoords() {
  const map = useMapInstance((s) => s.map);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!map) return;
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    let coord: number[] | null = null;
    const render = () => {
      raf = 0;
      if (!coord) {
        el.hidden = true;
        return;
      }
      const [lon, lat] = mapToLonLat(coord[0], coord[1]);
      if (!Number.isFinite(lon) || !Number.isFinite(lat) || Math.abs(lat) > 90) {
        el.hidden = true;
        return;
      }
      el.hidden = false;
      el.textContent = formatLonLat(lon, lat, 2);
    };
    const onMove = (evt: MapBrowserEvent) => {
      coord = evt.coordinate;
      if (!raf) raf = requestAnimationFrame(render);
    };
    const onLeave = () => {
      coord = null;
      if (!raf) raf = requestAnimationFrame(render);
    };
    map.on('pointermove', onMove);
    const viewport = map.getViewport();
    viewport.addEventListener('mouseleave', onLeave);
    return () => {
      map.un('pointermove', onMove);
      viewport.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(raf);
    };
  }, [map]);

  return (
    <div
      ref={ref}
      hidden
      aria-hidden
      // Logo acima da assinatura do pyForTraCC (tracking/TrackingFloat), que ocupa o canto.
      className="glass pointer-events-none absolute right-2 bottom-11 z-10 rounded-lg px-2 py-1 font-mono text-[11px] text-ink-100 tabular-nums"
    />
  );
}

export function MapControls(props: { mobile: boolean }) {
  const map = useMapInstance((s) => s.map);
  const hasHover = useHasHover();
  if (!map) return null;
  return (
    <>
      <div
        className={clsx(
          'glass absolute top-3 z-10 flex flex-col divide-y divide-ink-700/70 overflow-hidden rounded-xl shadow-lg',
          // No celular, 8 px da borda (e fora do entalhe, deitado), como a marca, o trilho e o pé (29/10/2026).
          props.mobile ? 'right-[calc(8px+env(safe-area-inset-right))]' : 'right-3',
        )}
        // No celular, faz parte da coluna que a ficha do mapa não cobre (map/popupArea).
        data-coluna-do-mapa={props.mobile ? '' : undefined}
        // No celular, abaixo da área segura do topo, como o chip e o resto da coluna (que já a descontavam).
        style={props.mobile ? { top: 'calc(env(safe-area-inset-top) + 12px)' } : undefined}
      >
        {!props.mobile && (
          <>
            <ControlButton label="Aproximar" onClick={() => zoomBy(map, 1)}>
              <Plus className="size-4" aria-hidden />
            </ControlButton>
            <ControlButton label="Afastar" onClick={() => zoomBy(map, -1)}>
              <Minus className="size-4" aria-hidden />
            </ControlButton>
          </>
        )}
        <ControlButton label="Voltar à Amazônia" onClick={() => flyHome(map)}>
          <House className="size-4" aria-hidden />
        </ControlButton>
      </div>
      {hasHover && !props.mobile && <CursorCoords />}
    </>
  );
}
